import { TrendingUp, Coins, ShieldAlert, Link2, Clock, ChevronRight, Package } from 'lucide-react';
import type { UseCaseItem } from '../../../types';
import BdrBattlecardSection from './BdrBattlecardSection';

interface Props {
  useCase: UseCaseItem;
  onClick: () => void;
}

const SCORE_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: 'bg-app-positive/10', text: 'text-app-positive' },
  mid: { bg: 'bg-app-warning/10', text: 'text-app-warning' },
  low: { bg: 'bg-app-critical/10', text: 'text-app-critical' },
};

function scoreColor(score: number) {
  if (score >= 7) return SCORE_COLORS.high;
  if (score >= 4) return SCORE_COLORS.mid;
  return SCORE_COLORS.low;
}

const RANK_STYLES: Record<number, { border: string; badge: string; badgeText: string }> = {
  1: { border: 'border-forest-300 ring-2 ring-forest-100', badge: 'bg-forest-600', badgeText: 'text-white' },
  2: { border: 'border-app-focus', badge: 'bg-app-focus', badgeText: 'text-white' },
  3: { border: 'border-app-line', badge: 'bg-app-muted', badgeText: 'text-white' },
  4: { border: 'border-app-line', badge: 'bg-app-muted', badgeText: 'text-white' },
};

function getProductNames(uc: UseCaseItem): string[] {
  if (uc.vendor_product_names && uc.vendor_product_names.length > 0) return uc.vendor_product_names;
  if (uc.vendor_product_name) return [uc.vendor_product_name];
  return [];
}

export default function UseCaseCard({ useCase, onClick }: Props) {
  const style = RANK_STYLES[useCase.rank] || RANK_STYLES[4];
  const scores = useCase.scores;
  const productNames = getProductNames(useCase);

  const scoreBadges = [
    { label: 'Revenue', value: scores.revenue, icon: TrendingUp },
    { label: 'Cost', value: scores.cost, icon: Coins },
    { label: 'Risk', value: scores.risk, icon: ShieldAlert },
    { label: 'Red Thread', value: scores.red_thread, icon: Link2 },
    { label: 'TTV', value: scores.time_to_value, icon: Clock },
  ];

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-app-surface rounded-lg border-2 shadow-none overflow-hidden transition hover:shadow-none hover:scale-[1.005] ${style.border}`}
    >
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${style.badge} ${style.badgeText}`}>
              #{useCase.rank}
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-bold text-app-ink leading-tight">{useCase.title}</h3>
              {productNames.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {productNames.map(name => (
                    <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-app-focus/10 text-app-focus text-[11px] font-semibold rounded-full border border-app-focus/30">
                      <Package className="w-3 h-3" />
                      {name}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-sm text-app-muted mt-1 line-clamp-2">{useCase.sales_hook}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-center px-3 py-1.5 bg-forest-50 rounded-lg border border-forest-200">
              <p className="text-xs font-medium text-forest-600 uppercase tracking-wider">Score</p>
              <p className="text-xl font-bold text-forest-700">{scores.weighted_total.toFixed(1)}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-app-line-strong" />
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {scoreBadges.map((badge) => {
            const color = scoreColor(badge.value);
            return (
              <span
                key={badge.label}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${color.bg} ${color.text}`}
              >
                <badge.icon className="w-3 h-3" />
                {badge.label}: {badge.value}
              </span>
            );
          })}
        </div>

        {useCase.bdr_battlecard && (
          <div className="mb-4">
            <BdrBattlecardSection battlecard={useCase.bdr_battlecard} compact />
          </div>
        )}

        {useCase.red_thread_mapping && (
          <div className="mb-3 p-2.5 bg-app-canvas rounded-lg">
            <p className="text-[11px] font-semibold text-app-muted uppercase tracking-wider mb-1">Red Thread</p>
            <p className="text-xs text-app-ink leading-relaxed">{useCase.red_thread_mapping}</p>
          </div>
        )}

        {useCase.why_now_trigger && (
          <div className="mb-3 p-2.5 bg-app-warning/10 rounded-lg border border-app-warning">
            <p className="text-[11px] font-semibold text-app-warning uppercase tracking-wider mb-1">Why Now</p>
            <p className="text-xs text-app-warning leading-relaxed">{useCase.why_now_trigger}</p>
          </div>
        )}

        {useCase.rank_rationale_bullets && useCase.rank_rationale_bullets.length > 0 && (
          <div className="space-y-1">
            {useCase.rank_rationale_bullets.map((bullet, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-forest-400 mt-1.5 flex-shrink-0" />
                <p className="text-xs text-app-muted leading-relaxed">{bullet}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}
